import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './PendingRegistrations.css';

interface Registration {
  id: number;
  business_name: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  customer_type: string;
  customer_type_display: string;
  referral_source: string;
  status: string;
  status_display: string;
  registration_step: string;
  email_verified: boolean;
  has_password: boolean;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string;
}

const PendingRegistrations: React.FC = () => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadRegistrations();
  }, [statusFilter]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/pending-registrations/?status=${statusFilter}`);
      setRegistrations(response.data);
    } catch (err) {
      console.error('Error loading registrations:', err);
      setError('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatPhone = (phone: string) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleApprove = async (registration: Registration) => {
    if (!window.confirm(`Are you sure you want to approve the registration for "${registration.business_name}"?\n\nThis will create a customer account and portal access.`)) {
      return;
    }

    try {
      setProcessing(true);
      await api.post(`/admin/pending-registrations/${registration.id}/approve/`);
      setSelectedRegistration(null);
      loadRegistrations();
    } catch (err: any) {
      console.error('Error approving registration:', err);
      alert(err.response?.data?.error || 'Failed to approve registration');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRegistration) return;

    try {
      setProcessing(true);
      await api.post(`/admin/pending-registrations/${selectedRegistration.id}/reject/`, {
        reason: rejectionReason
      });
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedRegistration(null);
      loadRegistrations();
    } catch (err: any) {
      console.error('Error rejecting registration:', err);
      alert(err.response?.data?.error || 'Failed to reject registration');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      default:
        return '';
    }
  };

  const getStepBadgeClass = (step: string) => {
    if (step === 'Ready for approval') return 'step-ready';
    if (step === 'Awaiting password setup') return 'step-password';
    if (step === 'Awaiting email verification') return 'step-email';
    return 'step-other';
  };

  if (loading && registrations.length === 0) {
    return (
      <div className="pending-registrations-page">
        <div className="loading">Loading registrations...</div>
      </div>
    );
  }

  return (
    <div className="pending-registrations-page">
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h1>Pending Customer Registrations</h1>
            <p className="page-subtitle">Review and approve new customer sign-ups</p>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="registrations-container">
        {/* Filters */}
        <div className="filters-section">
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="status-filter">Status:</label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="all">All</option>
              </select>
            </div>
            <div className="results-count">
              {registrations.length} registration{registrations.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Registrations Table */}
        <div className="table-container">
          <table className="registrations-table">
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Type</th>
                <th>Submitted</th>
                <th>Progress</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {registrations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="no-data">
                    {statusFilter === 'pending'
                      ? 'No pending registrations'
                      : 'No registrations found'}
                  </td>
                </tr>
              ) : (
                registrations.map((registration) => (
                  <tr key={registration.id} className="registration-row">
                    <td className="business-name">{registration.business_name}</td>
                    <td>{registration.contact_name}</td>
                    <td className="email">{registration.email}</td>
                    <td>{registration.customer_type_display}</td>
                    <td>{formatDate(registration.created_at)}</td>
                    <td>
                      <span className={`step-badge ${getStepBadgeClass(registration.registration_step)}`}>
                        {registration.registration_step}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-view"
                        onClick={() => setSelectedRegistration(registration)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Registration Detail Modal */}
      {selectedRegistration && !showRejectModal && (
        <div className="modal-overlay" onClick={() => setSelectedRegistration(null)}>
          <div className="modal-content registration-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Registration Details</h2>
              <button className="modal-close" onClick={() => setSelectedRegistration(null)}>
                &times;
              </button>
            </div>

            <div className="modal-body">
              <div className="registration-status-banner">
                <span className={`step-badge ${getStepBadgeClass(selectedRegistration.registration_step)}`}>
                  {selectedRegistration.registration_step}
                </span>
                <span className="submitted-date">
                  Submitted: {formatDate(selectedRegistration.created_at)}
                </span>
              </div>

              <div className="detail-section">
                <h3>Business Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Business Name:</label>
                    <span>{selectedRegistration.business_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Customer Type:</label>
                    <span>{selectedRegistration.customer_type_display}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Contact Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Contact Name:</label>
                    <span>{selectedRegistration.contact_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{selectedRegistration.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone:</label>
                    <span>{formatPhone(selectedRegistration.phone)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Address</h3>
                <div className="address-block">
                  {selectedRegistration.address && <div>{selectedRegistration.address}</div>}
                  <div>
                    {selectedRegistration.city}
                    {selectedRegistration.city && selectedRegistration.state && ', '}
                    {selectedRegistration.state} {selectedRegistration.zip_code}
                  </div>
                </div>
              </div>

              {selectedRegistration.referral_source && (
                <div className="detail-section">
                  <h3>How They Heard About Us</h3>
                  <div className="referral-block">{selectedRegistration.referral_source}</div>
                </div>
              )}

              {selectedRegistration.status === 'rejected' && selectedRegistration.rejection_reason && (
                <div className="detail-section rejection-section">
                  <h3>Rejection Reason</h3>
                  <div className="rejection-block">{selectedRegistration.rejection_reason}</div>
                  {selectedRegistration.reviewed_by && (
                    <div className="reviewed-info">
                      Rejected by {selectedRegistration.reviewed_by} on {formatDate(selectedRegistration.reviewed_at!)}
                    </div>
                  )}
                </div>
              )}

              {selectedRegistration.status === 'approved' && selectedRegistration.reviewed_by && (
                <div className="detail-section">
                  <h3>Approval Info</h3>
                  <div className="reviewed-info">
                    Approved by {selectedRegistration.reviewed_by} on {formatDate(selectedRegistration.reviewed_at!)}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {selectedRegistration.status === 'pending' && (
                <>
                  <button
                    className="btn btn-reject"
                    onClick={() => setShowRejectModal(true)}
                    disabled={processing}
                  >
                    Delete
                  </button>
                  {selectedRegistration.registration_step === 'Ready for approval' ? (
                    <button
                      className="btn btn-approve"
                      onClick={() => handleApprove(selectedRegistration)}
                      disabled={processing}
                    >
                      {processing ? 'Processing...' : 'Approve'}
                    </button>
                  ) : (
                    <span className="waiting-message">
                      Waiting for user to complete {selectedRegistration.registration_step.toLowerCase()}
                    </span>
                  )}
                </>
              )}
              <button className="btn btn-secondary" onClick={() => setSelectedRegistration(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRegistration && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header reject-header">
              <h2>Reject Registration</h2>
              <button className="modal-close" onClick={() => setShowRejectModal(false)}>
                &times;
              </button>
            </div>

            <div className="modal-body">
              <p className="reject-confirmation">
                Are you sure you want to reject the registration for <strong>{selectedRegistration.business_name}</strong>?
              </p>
              <div className="form-group">
                <label htmlFor="rejection-reason">Reason for Rejection (optional):</label>
                <textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter a reason for rejection..."
                  rows={4}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                className="btn btn-reject"
                onClick={handleReject}
                disabled={processing}
              >
                {processing ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingRegistrations;
